import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Select } from '@/components/ui/select';
import { useFeatureEntitlement } from '@/hooks/useFeatureEntitlements';
import { useEffectivePermissions } from '@/hooks/usePermissions';
import { useRole } from '@/hooks/useRole';
import { SHIFT_CSV_IMPORTS_FEATURE } from '@/lib/features';
import { getActiveSectionNavigation, navigationSectionLabels } from '@/lib/sectionNavigation';
import { cn } from '@/lib/utils';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';

export function DashboardSectionNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { role } = useRole();
  const permissions = useEffectivePermissions();
  const csvImportAccess = useFeatureEntitlement(SHIFT_CSV_IMPORTS_FEATURE);
  const access = {
    canUseCsvImports: csvImportAccess.data?.isEnabled ?? false,
    hasPermission: (permission: Parameters<typeof permissions.hasPermission>[0]) =>
      !permissions.isError && permissions.hasPermission(permission),
  };
  const sectionState = getActiveSectionNavigation(location.pathname, role, access);

  if (!sectionState || sectionState.items.length < 2) return null;

  const { section, items } = sectionState;

  return (
    <div
      className="mx-auto mb-6 w-full max-w-7xl border-b border-slate-200 dark:border-slate-700"
      aria-busy={permissions.isLoading || csvImportAccess.isLoading}
    >
      <div className="pb-3 sm:hidden">
        <Select
          aria-label={`${navigationSectionLabels[section]} section`}
          value={location.pathname}
          options={items.map((item) => ({ value: item.path, label: item.label }))}
          onChange={(event) => navigate(event.target.value)}
        />
      </div>

      <nav
        aria-label={`${navigationSectionLabels[section]} sections`}
        className="hidden flex-wrap gap-1 sm:flex"
      >
        {items.map((item) => {
          const icon = item.icon;

          return (
            <NavLink
              key={`${section}-${item.id}`}
              to={item.path}
              end
              className={({ isActive }) =>
                cn(
                  'relative flex h-12 items-center gap-2 rounded-t-md px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                  isActive
                    ? 'bg-brand-soft/60 text-brand after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:bg-brand'
                    : 'text-muted hover:bg-slate-100 hover:text-foreground dark:hover:bg-slate-800',
                )
              }
            >
              <WorklynxIcon name={icon} size={16} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
